import { createClient, type SupabaseClient } from "@supabase/supabase-js";

export interface OttoPublicConfig {
  service: string;
  laneId: string;
  portalUrl: string;
  workerOrigin: string;
  generatedAt: string;
  supabase: {
    configured: boolean;
    url: string;
    publishableKey: string;
    keySource: "secret_store" | "env_var" | "missing";
  };
  adminAuth: {
    mode: string;
    allowedEmailDomains: string[];
    allowlistConfigured: boolean;
    adminApiEnabled: boolean;
  };
  warnings: string[];
}

// Single source of truth for the worker origin. The portal bootstraps its
// Supabase config from this worker at runtime, so no build-time env vars are
// required for normal operation.
export const ottoWorkerBaseUrl = "https://hassett-keyme-ingest.deadsetgg.workers.dev";

export interface OttoBootstrap {
  config: OttoPublicConfig;
  supabase: SupabaseClient | null;
  configError: string;
}

/**
 * Fetch `/otto/public/config` from the Otto worker and create the Supabase
 * client. Returns null `supabase` when the worker is reachable but the
 * publishable key is not bound — in that case the UI shows a setup screen
 * instead of crashing.
 */
export async function bootstrapOttoClient(): Promise<OttoBootstrap> {
  const response = await fetch(`${ottoWorkerBaseUrl}/otto/public/config`, {
    cache: "no-store"
  });
  if (!response.ok) {
    throw new Error(`Otto worker config fetch failed: HTTP ${response.status}`);
  }
  const config = (await response.json()) as OttoPublicConfig;

  if (!config.supabase.configured || !config.supabase.url || !config.supabase.publishableKey) {
    return {
      config,
      supabase: null,
      configError:
        config.warnings[0] ??
        "Supabase publishable key is not bound on the Otto worker. Add it to Cloudflare Secret Store and redeploy."
    };
  }

  const supabase = createClient(config.supabase.url, config.supabase.publishableKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  });

  return { config, supabase, configError: "" };
}
